const BaseEntry = require('./BaseEntry');
const { newGuid } = require('../../../utils');
const getConstants = require('../../../utils/getConstants');
const constants = getConstants();
const { answerStates, repeaterCadenceTypes, repeaterFormItemTypes, formItemTypes } = constants;
const moment = require('moment-timezone');
const DomainException = require('../../DomainException');
const classifyRepeaterEntryMode = require('../utilities/classifyRepeaterEntryMode');
const evaluateRepeaterAddPossibility = require('../utilities/evaluateRepeaterAddPossibility');

class RepeaterEntry extends BaseEntry {
   constructor(aggregateRoot, componentData, items, entries, ruleEvaluator) {
      super(aggregateRoot, componentData, items, entries, ruleEvaluator);
   }

   getDataSourceRef() {
      if (!this.isOfListType()) return null;

      const binding = this.item && this.item.options ? this.item.options.binding : null;
      if (!binding || !binding.noDataSource) return null;

      return {
         noDataSource: binding.noDataSource,
         idField: binding.idField || 'id',
         txtField: binding.txtField || 'txt'
      };
   }

   getRepeaterGroups() {
      const res = (this.children || []).filter((c) => c && c.item && c.item.idItemType === formItemTypes.GROUP.id);
      return res;
   }

   lock() {
      const item = this.item;
      const { state } = item;
      item.state = state | answerStates.LOCKED;

      for (var i = 0; i < this.children.length; i++) {
         const e = this.children[i];
         e.lock();
      }
   }

   unlock() {
      const item = this.item;
      const { state } = item;
      item.state = state & ~answerStates.LOCKED;

      for (var i = 0; i < this.children.length; i++) {
         const e = this.children[i];
         e.unlock();
      }
   }

   isAddPossible() {
      return evaluateRepeaterAddPossibility({
         item: this.item,
         childItems: (this.children || []).map((child) => child && child.item).filter(Boolean)
      });
   }

   getNextRepeaterGroupMeta() {
      const possibility = this.isAddPossible();

      if (!possibility.canAdd) {
         return {
            ...possibility,
            response: null,
            label: null,
            idFormItem: this.item.idFormItem,
            id: this.item.id
         };
      }

      const label = this.getNextLabel();
      let response = null;

      if (this.isOfListType()) {
         const nextRepeaterOption = this.getNextRepeaterOption();
         if (!nextRepeaterOption) {
            throw new DomainException('Cannot add Repeater entry because list options are unavailable.');
         }
         response = { no: 1, upd: moment.utc().toDate(), val: nextRepeaterOption.id.toString() };
      } else {
         response = { no: 1, upd: moment.utc().toDate(), val: newGuid().toString() };
      }

      return {
         ...possibility,
         response,
         label,
         idFormItem: this.item.idFormItem,
         id: this.item.id
      };
   }

   getNextLabel() {
      const nextNo = this.children.length + 1;
      let label = `Entry #${nextNo}`;

      if (this.isOfListType()) {
         const nextRepeaterOption = this.getNextRepeaterOption();
         if (nextRepeaterOption) {
            label = nextRepeaterOption.txt;
         }
      }
      return label;
   }

   getNextRepeaterOption() {
      const listOptions = this.item.options && this.item.options.listOptions;
      if (!Array.isArray(listOptions)) {
         return null;
      }

      const consumedIds = new Set(
         (this.children || [])
            .map((child) => child && child.item && child.item.response && child.item.response.val)
            .filter((value) => value != null)
            .map((value) => String(value))
      );

      return listOptions.find((option) => option && option.id != null && !consumedIds.has(String(option.id))) || null;
   }

   requiresExactEntries() {
      return this.item.options && this.item.options.idRepeaterCadence === repeaterCadenceTypes.EXACTLY.id;
   }

   requiresAtMostEntries() {
      return this.item.options && this.item.options.idRepeaterCadence === repeaterCadenceTypes.AT_MOST.id;
   }

   requiresAtLeastEntries() {
      return this.item.options && this.item.options.idRepeaterCadence === repeaterCadenceTypes.AT_LEAST.id;
   }

   requiresAnyNoOfEntries() {
      return this.item.options && this.item.options.idRepeaterCadence === repeaterCadenceTypes.ANY_NUMBER_OF.id;
   }

   requiresEntriesForEachItem() {
      if (!this.item.options) return false;

      const classification = classifyRepeaterEntryMode({
         idRepeaterType: this.item.options.idRepeaterType,
         entryMode: this.item.options.entryMode,
         idRepeaterCadence: this.item.options.idRepeaterCadence,
         timesToRepeat: this.item.options.timesToRepeat
      });

      return (
         classification.kind === classifyRepeaterEntryMode.kinds.LEGACY_FOREACH ||
         classification.kind === classifyRepeaterEntryMode.kinds.CANONICAL_FULL_LIST
      );
   }

   validateAtLeastEntries() {
      const item = this.item;
      const { state } = item;
      let isInvalid = false;

      let isSkipped = (state & answerStates.SKIPPED) == answerStates.SKIPPED;

      if (this.item.options && this.item.options.timesToRepeat > this.children.length && !isSkipped) {
         isInvalid = true;
      }

      return isInvalid;
   }

   validateAtMostEntries() {
      const item = this.item;
      const { state } = item;
      let isInvalid = false;

      let isSkipped = (state & answerStates.SKIPPED) == answerStates.SKIPPED;

      if (this.item.options && this.item.options.timesToRepeat < this.children.length && !isSkipped) {
         isInvalid = true;
      }

      return isInvalid;
   }

   validateExactEntries() {
      const item = this.item;
      const { state } = item;
      let isInvalid = false;

      let isSkipped = (state & answerStates.SKIPPED) == answerStates.SKIPPED;

      if (this.item.options && this.item.options.timesToRepeat !== this.children.length && !isSkipped) {
         isInvalid = true;
      }

      return isInvalid;
   }

   validateAnyNumberEntries() {
      const item = this.item;
      const { state } = item;
      let isInvalid = false;

      let isSkipped = (state & answerStates.SKIPPED) == answerStates.SKIPPED;

      if (this.item.options && 0 === this.children.length && !isSkipped) {
         isInvalid = true;
      }

      return isInvalid;
   }

   validateEntriesForEachItem() {
      const { state } = this.item;
      const isSkipped = (state & answerStates.SKIPPED) == answerStates.SKIPPED;
      if (isSkipped) return false;

      const options = this.item.options || {};
      const classification = classifyRepeaterEntryMode({
         idRepeaterType: options.idRepeaterType,
         entryMode: options.entryMode,
         idRepeaterCadence: options.idRepeaterCadence,
         timesToRepeat: options.timesToRepeat
      });

      if (classification.kind === classifyRepeaterEntryMode.kinds.CANONICAL_FULL_LIST) {
         return this.item.repeaterMaterialized !== true;
      }

      return !!(this.item.options && this.item.options.listOptions.length !== this.children.length);
   }

   isOfListType() {
      return this.item.options && this.item.options.idRepeaterType == repeaterFormItemTypes.LIST.id;
   }

   isOfFreeFlowType() {
      return this.item.options && this.item.options.idRepeaterType == repeaterFormItemTypes.FREE_FLOW.id;
   }

   ensureIsOfListType(message) {
      message = message || 'Cannot perform this operation on an item which is not a list type.';
      if (!this.isOfListType()) {
         throw new DomainException(message);
      }
   }

   isSkipped() {
      return (this.item.state & answerStates.SKIPPED) === answerStates.SKIPPED;
   }

   isValid() {
      let isInvalid = false;

      switch (true) {
         case this.requiresExactEntries():
            isInvalid = this.validateExactEntries();
            break;
         case this.requiresAtMostEntries():
            isInvalid = this.validateAtMostEntries();
            break;
         case this.requiresAtLeastEntries():
            isInvalid = this.validateAtLeastEntries();
            break;
         case this.requiresAnyNoOfEntries():
            isInvalid = this.validateAnyNumberEntries();
            break;
         case this.requiresEntriesForEachItem():
            isInvalid = this.validateEntriesForEachItem();
            break;

         default:
            isInvalid = false;
            break;
      }

      return !isInvalid;
   }

   validateChildren() {
      let isValid = true;

      for (var i = 0; i < this.children.length; i++) {
         const e = this.children[i];
         if (!e.validate()) {
            isValid = false;
         }
      }
      return isValid;
   }

   hasFailures() {
      if (this.isSkipped()) {
         return false;
      }

      let isFailed = false;

      for (var i = 0; i < this.children.length; i++) {
         const e = this.children[i];
         if (e.hasFailures()) {
            isFailed = true;
         }
      }
      return isFailed;
   }

   countFlags() {
      if (this.isSkipped()) {
         return {
            flagged: false,
            noOfFlags: 0
         };
      }

      let flagged = false;
      let noOfFlags = 0;

      for (var i = 0; i < this.children.length; i++) {
         const e = this.children[i];

         const flagResult = e.countFlags();

         if (flagResult.flagged) {
            flagged = true;
            noOfFlags = noOfFlags + (flagResult.noOfFlags || 1);
         }
      }

      return {
         flagged,
         noOfFlags
      };
   }

   refreshValidityFromChildren() {
      if (this.isSkipped()) {
         return this.setCurrentValidity(true);
      }

      const isValid = this.isValid() && this.areImmediateChildrenCurrentlyValid();
      return this.setCurrentValidity(isValid);
   }

   validate() {
      if (this.isSkipped()) {
         return this.setCurrentValidity(true);
      }

      let isValid = this.isValid();

      if (isValid) {
         isValid = this.validateChildren();
      }

      return this.setCurrentValidity(isValid);
   }

   score() {
      if (this.isSkipped()) {
         if (this.item) {
            this.item.score = null;
            this.item.maxScore = null;
         }

         return {
            score: null,
            maxScore: null
         };
      }

      let overallScore = null;
      let overallMaxScore = null;

      for (var i = 0; i < this.children.length; i++) {
         const e = this.children[i];
         const scoreResult = e.score();

         overallScore =
            scoreResult.score == null ? overallScore : overallScore == null ? scoreResult.score : overallScore + scoreResult.score;

         overallMaxScore =
            scoreResult.maxScore == null
               ? overallMaxScore
               : overallMaxScore == null
               ? scoreResult.maxScore
               : overallMaxScore + scoreResult.maxScore;
      }

      if (this.item) {
         this.item.score = overallScore;
         this.item.maxScore = overallMaxScore;
      }

      return {
         score: overallScore,
         maxScore: overallMaxScore
      };
   }
}

module.exports = RepeaterEntry;
